/**
 * Feeder bundle budget label math (FLY-5300).
 *
 * For Maldives charter bundle feeder flights, customer-facing prices on tiles and
 * upsell modal options render as a `+/-$X` delta relative to the FDA-derived bundle
 * budget. Values are aggregate across paying pax (matches `feederEstimatedPrice` and
 * `price.all.totalFare` conventions on `App.JourneyV2`).
 *
 * Rules:
 * - Departing page (tile and per-option in the single upsell modal): use the
 *   departing-leg base/buffer (with returning-leg fallback if departing absent).
 *   Per-option delta is `0` when the option fare sits inside `base + buffer`,
 *   otherwise `optionFare - base`. Never negative.
 * - Returning page (tile and per-option in the returning single upsell modal):
 *   reference the whole-trip base/buffer. Delta is computed off the projected
 *   total (`selectedDepartingFare + optionFare`) and reduces by the delta that
 *   was already displayed for the selected departing option, so the visible
 *   numbers across both pages add up to the actual excess once.
 */

export function computeDepartingFeederDelta({
  optionFare,
  baseBudget,
  bufferBudget = 0,
}: {
  optionFare: number
  baseBudget: number
  bufferBudget?: number
}): number {
  if (baseBudget <= 0) return 0
  if (optionFare <= baseBudget + bufferBudget) return 0
  const delta = optionFare - baseBudget
  return delta > 0 ? delta : 0
}

/**
 * Upsell modal (departing OR returning): per-option delta is the gap between the
 * option's own page-formula delta and the tile's own page-formula delta. The tile
 * always shows `+A$0` for its own fare family because their deltas cancel.
 *
 * Callers pre-compute `optionDelta` and `tileDelta` using the page-appropriate
 * formula (`computeDepartingFeederDelta` on the Departing page, or
 * `computeReturningFeederDelta` on the Returning page with the constant
 * `selectedDepartingFare`/`shownDepartingDelta` shared between tile and options).
 *
 * Cases:
 * - Tile within budget (tileDelta = 0): each option falls back to its own
 *   page-formula delta. Within budget → `+A$0`. Over buffer → page-formula value.
 * - Tile over buffer (tileDelta > 0): cheaper options clamp to `+A$0`; options
 *   also over budget show the additional spend the user has not already seen on
 *   the tile.
 */
export function computeUpsellModalDelta({
  optionDelta,
  tileDelta,
}: {
  optionDelta: number
  tileDelta: number
}): number {
  const delta = optionDelta - tileDelta
  return delta > 0 ? delta : 0
}

export function computeReturningFeederDelta({
  selectedDepartingFare = 0,
  optionFare,
  baseBudget,
  bufferBudget = 0,
  shownDepartingDelta = 0,
}: {
  selectedDepartingFare?: number
  optionFare: number
  baseBudget: number
  bufferBudget?: number
  shownDepartingDelta?: number
}): number {
  if (baseBudget <= 0) return 0
  const refPrice = selectedDepartingFare + optionFare
  if (refPrice <= baseBudget + bufferBudget) {
    // Whole trip is within budget at summary level. The departing page already
    // showed `+shownDepartingDelta` (possibly 0), so balance it out here.
    return shownDepartingDelta === 0 ? 0 : -shownDepartingDelta
  }
  const delta = refPrice - baseBudget - shownDepartingDelta
  // Never go below the symmetric `-shownDepartingDelta` floor: the worst-case
  // visible value for an in-budget trip.
  if (delta < -shownDepartingDelta) {
    return shownDepartingDelta === 0 ? 0 : -shownDepartingDelta
  }
  return delta
}

export function resolveDepartingFeederReference({
  bundleBudget,
  optionLegBudget,
  optionLegBuffer,
}: {
  bundleBudget: App.Checkout.FeederBundleBudget
  optionLegBudget?: number
  optionLegBuffer?: number
}): { baseBudget: number; bufferBudget: number } {
  if (optionLegBudget !== undefined && optionLegBudget > 0) {
    return {
      baseBudget: optionLegBudget,
      bufferBudget: optionLegBuffer ?? 0,
    }
  }
  if (bundleBudget.departingLegBudget && bundleBudget.departingLegBudget > 0) {
    return {
      baseBudget: bundleBudget.departingLegBudget,
      bufferBudget: bundleBudget.departingLegBuffer ?? 0,
    }
  }
  if (bundleBudget.returningLegBudget && bundleBudget.returningLegBudget > 0) {
    return {
      baseBudget: bundleBudget.returningLegBudget,
      bufferBudget: bundleBudget.returningLegBuffer ?? 0,
    }
  }
  return { baseBudget: 0, bufferBudget: 0 }
}

export function getBudgetedFeederLegFare(fare: number, legBudget: number, legBuffer: number = 0): number {
  if (fare <= legBudget + legBuffer) return legBudget
  return fare
}

export function getFirstLegFeederBudgetReference({
  optionBudget,
  legBudget,
  tripBudget,
}: {
  optionBudget?: number
  legBudget?: number
  tripBudget?: number
}): number {
  if (optionBudget !== undefined && optionBudget > 0) return optionBudget
  if (legBudget !== undefined && legBudget > 0) return legBudget
  if (tripBudget !== undefined && tripBudget > 0) return tripBudget
  return 0
}
